import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  Phone, ChatCircleText, GlobeHemisphereEast, Printer, SimCard, ArrowsClockwise,
  ShieldCheck, VideoCamera, Users, Broadcast, Waveform, FilmSlate, ArrowRight, CheckCircle,
  WhatsappLogo, MagnifyingGlass, Star,
} from "@phosphor-icons/react";
import { money } from "@/lib/format";

const ICONS = {
  Phone, ChatCircleText, GlobeHemisphereEast, Printer, SimCard, ArrowsClockwise,
  ShieldCheck, VideoCamera, Users, Broadcast, Waveform, FilmSlate, WhatsappLogo, MagnifyingGlass,
};

const ProductCard = ({ p, navigate }) => {
  const Icon = ICONS[p.icon] || GlobeHemisphereEast;
  return (
    <div data-testid={`product-${p.id}`}
      className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-primary"><Icon size={24} weight="duotone" /></div>
        {p.popular && (
          <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
            <Star size={11} weight="fill" /> Popular
          </span>
        )}
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold">{p.name}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
      <ul className="mt-3 grid flex-1 gap-1.5">
        {p.features.slice(0, 3).map((f, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle size={14} weight="fill" className="shrink-0 text-primary" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
        {p.addon
          ? <span className="text-sm"><span className="font-mono-metric font-semibold">{money(p.addon.price_cents, "USD")}</span><span className="text-muted-foreground"> · {p.addon.unit}</span></span>
          : <span className="text-xs text-muted-foreground">Pay only for usage</span>}
        <button data-testid={`product-open-${p.id}`} onClick={() => navigate(p.route)}
          className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
          Open <ArrowRight size={15} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default function Products() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["products-catalog"], queryFn: async () => (await api.get("/products/catalog")).data });
  const products = data?.products || [];
  const popular = products.filter((p) => p.popular);
  const rest = products.filter((p) => !p.popular);

  return (
    <div className="space-y-8" data-testid="products-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Products & Services</h1>
        <p className="mt-2 text-muted-foreground">Everything iYeneh can do — pick a service and get started in one tap.</p>
      </div>

      {popular.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <Star size={14} weight="fill" className="text-primary" /> Most popular
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {popular.map((p) => <ProductCard key={p.id} p={p} navigate={navigate} />)}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">All services</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => <ProductCard key={p.id} p={p} navigate={navigate} />)}
        </div>
      </div>
    </div>
  );
}
